import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@/components/ui/toggle-group";
import type { ProductCategory } from "@/lib/shop";

export type ShopSort = "nyeste" | "pris-stigende" | "pris-synkende";
export type ShopCategoryFilter = ProductCategory | "alle";

interface ShopFiltersProps {
  category: ShopCategoryFilter;
  sort: ShopSort;
  query: string;
  onCategoryChange: (next: ShopCategoryFilter) => void;
  onSortChange: (next: ShopSort) => void;
  onQueryChange: (next: string) => void;
  resultCount: number;
}

export function ShopFilters({
  category,
  sort,
  query,
  onCategoryChange,
  onSortChange,
  onQueryChange,
  resultCount,
}: ShopFiltersProps) {
  return (
    <section
      aria-label="Filter og sortering"
      className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-4 md:p-5"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">
            Kategori
          </p>
          <ToggleGroup
            type="single"
            value={category}
            onValueChange={(value) => {
              if (!value) return;
              onCategoryChange(value as ShopCategoryFilter);
            }}
            className="mt-2 flex-wrap justify-start gap-2"
          >
            <ToggleGroupItem
              value="alle"
              className="rounded-full border border-border bg-background px-4 py-1.5 text-sm font-body data-[state=on]:border-primary data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
            >
              Alle
            </ToggleGroupItem>
            <ToggleGroupItem
              value="mennesker"
              className="rounded-full border border-border bg-background px-4 py-1.5 text-sm font-body data-[state=on]:border-primary data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
            >
              Til deg
            </ToggleGroupItem>
            <ToggleGroupItem
              value="hunder"
              className="rounded-full border border-border bg-background px-4 py-1.5 text-sm font-body data-[state=on]:border-primary data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
            >
              Til hunden din
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="flex flex-col gap-2 md:items-end">
          <label
            htmlFor="shop-sort"
            className="text-xs uppercase tracking-[0.2em] text-primary font-semibold"
          >
            Sortering
          </label>
          <Select
            value={sort}
            onValueChange={(value) => onSortChange(value as ShopSort)}
          >
            <SelectTrigger
              id="shop-sort"
              className="w-full rounded-full md:w-56"
            >
              <SelectValue placeholder="Velg sortering" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="nyeste">Nyeste først</SelectItem>
              <SelectItem value="pris-stigende">Pris: lav til høy</SelectItem>
              <SelectItem value="pris-synkende">Pris: høy til lav</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label
          htmlFor="shop-search"
          className="text-xs uppercase tracking-[0.2em] text-primary font-semibold"
        >
          Søk
        </label>
        <div className="relative">
          <Search
            size={16}
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            id="shop-search"
            type="search"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Søk etter produkt…"
            aria-label="Søk etter produkt"
            className="rounded-full pl-10"
          />
        </div>
        <p className="text-xs text-muted-foreground font-body" aria-live="polite">
          {resultCount === 0
            ? "Ingen produkter matcher søket"
            : resultCount === 1
              ? "1 produkt"
              : `${resultCount} produkter`}
        </p>
      </div>
    </section>
  );
}
